import React, { useState } from 'react';
import api from '../utils/api';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-card">
      <div className="px-5 py-4 border-b border-secondary-100">
        <h3 className="font-semibold text-secondary-900">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-secondary-400 mb-0.5">{label}</p>
      <p className="text-sm text-secondary-800 font-medium">{value ?? '--'}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    registered: 'bg-blue-50 text-blue-700',
    claimed: 'bg-amber-50 text-amber-700',
    provisioned: 'bg-emerald-50 text-emerald-700',
    complete: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-secondary-100 text-secondary-500'}`}>
      {status}
    </span>
  );
}

function SecretDisplay({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <p className="text-xs text-amber-700 font-semibold mb-1">
        {label} -- Save this now, it will not be shown again!
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm font-mono text-secondary-900 break-all select-all">
          {value}
        </code>
        <button
          onClick={handleCopy}
          className="px-3 py-2 text-xs font-medium rounded-lg transition bg-amber-100 text-amber-800 hover:bg-amber-200 whitespace-nowrap"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default function Setup() {
  /* ---- Register Device ---- */
  const [regForm, setRegForm] = useState({
    device_id: '',
    firmware_version: '',
    method: 'manual',
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regApiKey, setRegApiKey] = useState('');

  /* ---- Serial Lookup ---- */
  const [serialInput, setSerialInput] = useState('');
  const [serialResult, setSerialResult] = useState(null);
  const [serialLoading, setSerialLoading] = useState(false);
  const [serialError, setSerialError] = useState('');

  /* ---- Provisioning Status ---- */
  const [statusDeviceId, setStatusDeviceId] = useState('');
  const [provStatus, setProvStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');

  /* ---- Workflow ---- */
  const [wfDeviceId, setWfDeviceId] = useState('');
  const [wfPatientId, setWfPatientId] = useState('');
  const [wfLoading, setWfLoading] = useState('');
  const [wfError, setWfError] = useState('');
  const [wfSuccess, setWfSuccess] = useState('');
  const [wfSecret, setWfSecret] = useState(null); // { label, value }

  /* ========== Register Device ========== */
  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegApiKey('');
    setRegLoading(true);
    try {
      const payload = { ...regForm };
      if (!payload.firmware_version) delete payload.firmware_version;
      const { data } = await api.post('/provisioning/register', payload);
      setRegApiKey(data.api_key || data.apiKey || '');
      setRegForm({ device_id: '', firmware_version: '', method: 'manual' });
    } catch (err) {
      setRegError(err.response?.data?.error || err.message || 'Registration failed');
    } finally {
      setRegLoading(false);
    }
  };

  /* ========== Serial Lookup ========== */
  const handleSerialLookup = async (e) => {
    e.preventDefault();
    setSerialError('');
    setSerialResult(null);
    if (!serialInput.trim()) return;
    setSerialLoading(true);
    try {
      const { data } = await api.get(`/provisioning/serial/${serialInput.trim()}`);
      setSerialResult(data);
    } catch (err) {
      setSerialError(err.response?.data?.error || err.message || 'Lookup failed');
    } finally {
      setSerialLoading(false);
    }
  };

  /* ========== Provisioning Status ========== */
  const handleStatusLookup = async (e) => {
    e.preventDefault();
    setStatusError('');
    setProvStatus(null);
    if (!statusDeviceId.trim()) return;
    setStatusLoading(true);
    try {
      const { data } = await api.get(`/provisioning/${statusDeviceId.trim()}/status`);
      setProvStatus(data);
    } catch (err) {
      setStatusError(err.response?.data?.error || err.message || 'Lookup failed');
    } finally {
      setStatusLoading(false);
    }
  };

  /* ========== Workflow Actions ========== */
  const runWorkflow = async (action, extraPayload) => {
    if (!wfDeviceId.trim()) {
      setWfError('Enter a device ID first.');
      return;
    }
    setWfError('');
    setWfSuccess('');
    setWfSecret(null);
    setWfLoading(action);
    try {
      switch (action) {
        case 'claim':
          if (!wfPatientId.trim()) { setWfError('Patient ID is required for claim.'); setWfLoading(''); return; }
          await api.post(`/provisioning/${wfDeviceId.trim()}/claim`, { patient_id: wfPatientId.trim() });
          setWfSuccess('Device claimed successfully.');
          break;
        case 'provision':
          await api.post(`/provisioning/${wfDeviceId.trim()}/provision`);
          setWfSuccess('Device marked as provisioned.');
          break;
        case 'complete':
          await api.post(`/provisioning/${wfDeviceId.trim()}/complete`);
          setWfSuccess('Setup completed.');
          break;
        case 'generate-api-key': {
          const res = await api.post(`/provisioning/${wfDeviceId.trim()}/generate-api-key`);
          setWfSecret({ label: 'API Key', value: res.data.api_key || res.data.apiKey || JSON.stringify(res.data) });
          setWfSuccess('API key generated.');
          break;
        }
        case 'rotate-key': {
          const res = await api.post(`/provisioning/${wfDeviceId.trim()}/rotate-key`);
          setWfSecret({ label: 'New API Key', value: res.data.api_key || res.data.apiKey || JSON.stringify(res.data) });
          setWfSuccess('API key rotated.');
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setWfError(err.response?.data?.error || err.message || `${action} failed`);
    } finally {
      setWfLoading('');
    }
  };

  /* ========== Render ========== */
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Device Setup</h1>
        <p className="text-secondary-500 text-sm mt-1">Register, provision, and manage device lifecycle</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ---- Register Device ---- */}
        <Section title="Register Device">
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Device ID *</label>
                <input
                  name="device_id" value={regForm.device_id} onChange={handleRegChange} required
                  placeholder="CIC-001"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Firmware Version</label>
                <input
                  name="firmware_version" value={regForm.firmware_version} onChange={handleRegChange}
                  placeholder="Optional, e.g. 4.1.0"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Method</label>
                <select
                  name="method" value={regForm.method} onChange={handleRegChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  <option value="manual">Manual</option>
                  <option value="ble_ios">BLE iOS</option>
                  <option value="local_ap">Local AP</option>
                  <option value="api">API</option>
                </select>
              </div>
            </div>
            {regError && <p className="text-sm text-red-600">{regError}</p>}
            <button
              type="submit" disabled={regLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
            >
              {regLoading ? 'Registering...' : 'Register Device'}
            </button>
            {regApiKey && <SecretDisplay label="API Key" value={regApiKey} />}
          </form>
        </Section>

        {/* ---- Lookup Device ID ---- */}
        <Section title="Lookup Device ID">
          <form onSubmit={handleSerialLookup} className="space-y-4">
            <div>
              <label className="block text-xs text-secondary-500 mb-1">Device ID</label>
              <input
                value={serialInput} onChange={(e) => setSerialInput(e.target.value)}
                placeholder="CIC-AABBCCDDEEFF"
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            {serialError && <p className="text-sm text-red-600">{serialError}</p>}
            <button
              type="submit" disabled={serialLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
            >
              {serialLoading ? 'Looking up...' : 'Lookup'}
            </button>
            {serialResult && (
              <div className="mt-3 p-4 bg-secondary-50 rounded-xl">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Valid" value={serialResult.valid != null ? (serialResult.valid ? 'Yes' : 'No') : '--'} />
                  <Field label="Device ID" value={serialResult.device_id} />
                  <Field label="Provisioning Status" value={serialResult.provisioning_status} />
                </div>
              </div>
            )}
          </form>
        </Section>

        {/* ---- Provisioning Status Lookup ---- */}
        <Section title="Provisioning Status">
          <form onSubmit={handleStatusLookup} className="space-y-4">
            <div>
              <label className="block text-xs text-secondary-500 mb-1">Device ID</label>
              <input
                value={statusDeviceId} onChange={(e) => setStatusDeviceId(e.target.value)}
                placeholder="CIC-001"
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            {statusError && <p className="text-sm text-red-600">{statusError}</p>}
            <button
              type="submit" disabled={statusLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
            >
              {statusLoading ? 'Loading...' : 'Get Status'}
            </button>
            {provStatus && (
              <div className="mt-3 p-4 bg-secondary-50 rounded-xl">
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(provStatus).map(([key, val]) => (
                    <Field
                      key={key}
                      label={key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      value={typeof val === 'boolean' ? (val ? 'Yes' : 'No') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                    />
                  ))}
                </div>
              </div>
            )}
          </form>
        </Section>

        {/* ---- Provisioning Workflow ---- */}
        <Section title="Provisioning Workflow">
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-secondary-500 mb-1">Device ID *</label>
              <input
                value={wfDeviceId} onChange={(e) => setWfDeviceId(e.target.value)}
                placeholder="CIC-001"
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-secondary-500 mb-1">Patient ID (for Claim)</label>
              <input
                value={wfPatientId} onChange={(e) => setWfPatientId(e.target.value)}
                placeholder="patient-uuid"
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            {wfError && <p className="text-sm text-red-600">{wfError}</p>}
            {wfSuccess && <p className="text-sm text-emerald-600">{wfSuccess}</p>}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runWorkflow('claim')}
                disabled={!!wfLoading}
                className="px-3 py-2 text-xs font-medium rounded-lg transition bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {wfLoading === 'claim' ? 'Claiming...' : 'Claim Device'}
              </button>
              <button
                onClick={() => runWorkflow('provision')}
                disabled={!!wfLoading}
                className="px-3 py-2 text-xs font-medium rounded-lg transition bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                {wfLoading === 'provision' ? 'Provisioning...' : 'Mark Provisioned'}
              </button>
              <button
                onClick={() => runWorkflow('complete')}
                disabled={!!wfLoading}
                className="px-3 py-2 text-xs font-medium rounded-lg transition bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              >
                {wfLoading === 'complete' ? 'Completing...' : 'Complete Setup'}
              </button>
              <button
                onClick={() => runWorkflow('generate-api-key')}
                disabled={!!wfLoading}
                className="px-3 py-2 text-xs font-medium rounded-lg transition bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
              >
                {wfLoading === 'generate-api-key' ? 'Generating...' : 'Generate API Key'}
              </button>
              <button
                onClick={() => runWorkflow('rotate-key')}
                disabled={!!wfLoading}
                className="px-3 py-2 text-xs font-medium rounded-lg transition bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                {wfLoading === 'rotate-key' ? 'Rotating...' : 'Rotate API Key'}
              </button>
            </div>

            {wfSecret && <SecretDisplay label={wfSecret.label} value={wfSecret.value} />}
          </div>
        </Section>
      </div>
    </div>
  );
}
